import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { CheckCircle2, Upload, FileText, Shield } from 'lucide-react';
import { useBranding } from '@/lib/branding';

function SignatureCanvas({ value, onChange }) {
  const canvasRef = React.useRef(null);
  const [drawing, setDrawing] = React.useState(false);
  const [hasSigned, setHasSigned] = React.useState(!!value);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Set canvas resolution for high DPI
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    // Draw signature line
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.fillStyle = '#9ca3af';
    ctx.font = '11px sans-serif';
    ctx.fillText('Sign here', 20, rect.height - 14);
    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = value;
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setDrawing(true);
    setHasSigned(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const endDraw = () => {
    if (!drawing) return;
    setDrawing(false);
    const canvas = canvasRef.current;
    onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.fillStyle = '#9ca3af';
    ctx.font = '11px sans-serif';
    ctx.fillText('Sign here', 20, rect.height - 14);
    setHasSigned(false);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <div className={`border-2 rounded-lg overflow-hidden bg-white transition-colors ${drawing ? 'border-primary' : hasSigned ? 'border-green-400' : 'border-dashed border-gray-300'}`} style={{ touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '150px' }}
          className="cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
      </div>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          {hasSigned && value ? (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Signature captured</span>
          ) : (
            <span className="text-xs text-muted-foreground">Draw your signature using mouse or finger</span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={clear} type="button" data-testid="clear-signature">Clear</Button>
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ['Personal Details', 'Work Eligibility', 'Employment Details', 'Compliance Checks', 'Document Uploads', 'Bank & Payroll', 'Declarations', 'Review & Submit'];
const STEP_KEYS = ['personal_details', 'work_eligibility', 'employment_details', 'compliance_checks', 'documents', 'bank_details', 'declarations', 'review'];

const DOC_TYPES = [
  { key: 'RESUME', label: 'Resume', required: true },
  { key: 'PHOTO_ID', label: 'Photo ID', required: true },
  { key: 'NDIS_WORKER_SCREENING', label: 'NDIS Worker Screening', required: true },
  { key: 'WWCC', label: 'Working With Children Check', required: true },
  { key: 'POLICE_CHECK', label: 'Police Check', required: true },
  { key: 'FIRST_AID', label: 'First Aid Certificate', required: true },
  { key: 'CPR', label: 'CPR Certificate', required: true },
  { key: 'DRIVER_LICENCE', label: 'Driver Licence', required: false },
  { key: 'QUALIFICATIONS', label: 'Qualifications', required: false },
  { key: 'EMPLOYMENT_AGREEMENT', label: 'Employment Agreement', required: true },
  { key: 'CODE_OF_CONDUCT', label: 'Code of Conduct', required: true },
  { key: 'NDIS_DECLARATION', label: 'NDIS Declaration', required: true },
];

function StepNav({ current, steps, progress }) {
  return (
    <div className="space-y-2 mb-6">
      <div className="h-2 bg-muted rounded-full overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${progress}%` }} /></div>
      <div className="flex gap-1 flex-wrap">
        {steps.map((s, i) => (
          <Badge key={i} variant={i === current ? 'default' : i < current ? 'secondary' : 'outline'} className="text-xs">{i + 1}. {s}</Badge>
        ))}
      </div>
    </div>
  );
}

function FieldRow({ label, children, required }) {
  return (
    <div className="space-y-1">
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      {children}
    </div>
  );
}

export default function OnboardingPortalPage() {
  const { token } = useParams();
  const { organisationName } = useBranding();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/onboarding/${token}`);
      setData(res.data.data);
      setForm(res.data.data.form || {});
      setDocs(res.data.data.documents || []);
      if (res.data.data.form?.submitted_at) setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Invalid or expired link');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const updateField = (section, field, value) => {
    setForm(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };

  const saveStep = async (section) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('step', section);
      fd.append('data', JSON.stringify(form[section] || {}));
      await axios.post(`${API}/onboarding/save`, fd);
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const uploadDoc = async (docType, file, extraFields = {}) => {
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('document_type', docType);
      fd.append('file', file);
      Object.entries(extraFields).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await axios.post(`${API}/onboarding/document/upload`, fd);
      toast.success(`${docType} uploaded`);
      load();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Upload failed'); }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('token', token);
      await axios.post(`${API}/onboarding/submit`, fd);
      setSubmitted(true);
      toast.success('Onboarding submitted successfully!');
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Submit failed'); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="max-w-md"><CardContent className="py-8 text-center">
        <Shield className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-bold">Invalid Link</h2>
        <p className="text-muted-foreground mt-2">This onboarding link is invalid or has expired. Please contact your recruiter.</p>
      </CardContent></Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <Card className="max-w-md"><CardContent className="py-8 text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
        <h2 className="text-xl font-bold">Onboarding Submitted!</h2>
        <p className="text-muted-foreground mt-2">Thank you, {data.candidate?.first_name}. Your onboarding has been submitted. We'll review your documents and be in touch.</p>
      </CardContent></Card>
    </div>
  );

  const pd = form.personal_details || {};
  const we = form.work_eligibility || {};
  const ed = form.employment_details || {};
  const cc = form.compliance_checks || {};
  const bd = form.bank_details || {};
  const dc = form.declarations || {};
  const progress = form.progress_percent || 0;

  const renderStep = () => {
    switch (step) {
      case 0: return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="First Name" required><Input value={pd.first_name || data.candidate?.first_name || ''} onChange={e => updateField('personal_details', 'first_name', e.target.value)} /></FieldRow>
            <FieldRow label="Last Name" required><Input value={pd.last_name || data.candidate?.last_name || ''} onChange={e => updateField('personal_details', 'last_name', e.target.value)} /></FieldRow>
          </div>
          <FieldRow label="Date of Birth" required><Input type="date" value={pd.date_of_birth || ''} onChange={e => updateField('personal_details', 'date_of_birth', e.target.value)} /></FieldRow>
          <FieldRow label="Phone" required><Input value={pd.phone || data.candidate?.phone || ''} onChange={e => updateField('personal_details', 'phone', e.target.value)} /></FieldRow>
          <FieldRow label="Email" required><Input value={pd.email || data.candidate?.email || ''} onChange={e => updateField('personal_details', 'email', e.target.value)} /></FieldRow>
          <FieldRow label="Address" required><Input value={pd.address || ''} onChange={e => updateField('personal_details', 'address', e.target.value)} /></FieldRow>
          <div className="grid grid-cols-3 gap-3">
            <FieldRow label="City"><Input value={pd.city || ''} onChange={e => updateField('personal_details', 'city', e.target.value)} /></FieldRow>
            <FieldRow label="State"><Input value={pd.state || ''} onChange={e => updateField('personal_details', 'state', e.target.value)} /></FieldRow>
            <FieldRow label="Postcode"><Input value={pd.postcode || ''} onChange={e => updateField('personal_details', 'postcode', e.target.value)} /></FieldRow>
          </div>
          <div className="pt-3 border-t"><p className="text-sm font-medium mb-2">Emergency Contact</p></div>
          <FieldRow label="Emergency Contact Name" required><Input value={pd.emergency_name || ''} onChange={e => updateField('personal_details', 'emergency_name', e.target.value)} /></FieldRow>
          <FieldRow label="Emergency Contact Phone" required><Input value={pd.emergency_phone || ''} onChange={e => updateField('personal_details', 'emergency_phone', e.target.value)} /></FieldRow>
          <FieldRow label="Relationship"><Input value={pd.emergency_relationship || ''} onChange={e => updateField('personal_details', 'emergency_relationship', e.target.value)} /></FieldRow>
        </div>
      );
      case 1: return (
        <div className="space-y-3">
          <FieldRow label="Work Rights in Australia"><Select value={we.work_rights || ''} onValueChange={v => updateField('work_eligibility', 'work_rights', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="CITIZEN">Australian Citizen</SelectItem><SelectItem value="PR">Permanent Resident</SelectItem><SelectItem value="VISA">Visa Holder</SelectItem></SelectContent></Select></FieldRow>
          {we.work_rights === 'VISA' && <>
            <FieldRow label="Visa Type"><Input value={we.visa_type || ''} onChange={e => updateField('work_eligibility', 'visa_type', e.target.value)} /></FieldRow>
            <FieldRow label="Visa Expiry"><Input type="date" value={we.visa_expiry || ''} onChange={e => updateField('work_eligibility', 'visa_expiry', e.target.value)} /></FieldRow>
          </>}
          <FieldRow label="Nationality"><Input value={we.nationality || ''} onChange={e => updateField('work_eligibility', 'nationality', e.target.value)} /></FieldRow>
        </div>
      );
      case 2: return (
        <div className="space-y-3">
          <FieldRow label="Employment Type"><Select value={ed.employment_type || ''} onValueChange={v => updateField('employment_details', 'employment_type', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{['FULL_TIME','PART_TIME','CASUAL','CONTRACT'].map(t => <SelectItem key={t} value={t}>{t.replace('_',' ')}</SelectItem>)}</SelectContent></Select></FieldRow>
          <FieldRow label="Experience Years"><Input type="number" value={ed.experience_years || ''} onChange={e => updateField('employment_details', 'experience_years', e.target.value)} /></FieldRow>
          <div className="pt-2"><p className="text-sm font-medium mb-2">Support Experience</p>
            <div className="grid grid-cols-2 gap-2">{['SIL','Community Access','Personal Care','Behaviour Support','Medication Administration','Manual Handling'].map(s => (
              <label key={s} className="flex items-center gap-2 text-sm"><Checkbox checked={(ed.support_experience || []).includes(s)} onCheckedChange={c => { const curr = ed.support_experience || []; updateField('employment_details', 'support_experience', c ? [...curr, s] : curr.filter(x => x !== s)); }} />{s}</label>
            ))}</div>
          </div>
        </div>
      );
      case 3: return (
        <div className="space-y-3">
          {[['ndis_screening_number','NDIS Worker Screening Number','ndis_expiry','NDIS Expiry'],['wwcc_number','WWCC Number','wwcc_expiry','WWCC Expiry'],['police_check_number','Police Check Number','police_check_expiry','Police Check Expiry'],['first_aid_cert','First Aid Certificate','first_aid_expiry','First Aid Expiry'],['cpr_cert','CPR Certificate','cpr_expiry','CPR Expiry'],['driver_licence','Driver Licence Number','licence_expiry','Licence Expiry']].map(([numKey,numLabel,expKey,expLabel]) => (
            <div key={numKey} className="grid grid-cols-2 gap-3">
              <FieldRow label={numLabel}><Input value={cc[numKey] || ''} onChange={e => updateField('compliance_checks', numKey, e.target.value)} /></FieldRow>
              <FieldRow label={expLabel}><Input type="date" value={cc[expKey] || ''} onChange={e => updateField('compliance_checks', expKey, e.target.value)} /></FieldRow>
            </div>
          ))}
        </div>
      );
      case 4: return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Upload required documents. Accepted formats: PDF, JPG, PNG (max 10MB each).</p>
          {DOC_TYPES.map(dt => {
            const uploaded = docs.find(d => d.document_type === dt.key);
            return (
              <div key={dt.key} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  {uploaded ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <FileText className="h-5 w-5 text-muted-foreground" />}
                  <div><p className="text-sm font-medium">{dt.label} {dt.required && <span className="text-red-500">*</span>}</p>
                    {uploaded && <p className="text-xs text-green-600">{uploaded.file_name} — {uploaded.status}</p>}
                  </div>
                </div>
                <label className="cursor-pointer"><Button variant="outline" size="sm" asChild><span><Upload className="h-3 w-3 mr-1" />{uploaded ? 'Replace' : 'Upload'}</span></Button>
                  <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => { if (e.target.files?.[0]) uploadDoc(dt.key, e.target.files[0]); }} />
                </label>
              </div>
            );
          })}
        </div>
      );
      case 5: return (
        <div className="space-y-3">
          <FieldRow label="Account Name" required><Input value={bd.account_name || ''} onChange={e => updateField('bank_details', 'account_name', e.target.value)} /></FieldRow>
          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="BSB" required><Input value={bd.bsb || ''} onChange={e => updateField('bank_details', 'bsb', e.target.value)} maxLength={6} /></FieldRow>
            <FieldRow label="Account Number" required><Input value={bd.account_number || ''} onChange={e => updateField('bank_details', 'account_number', e.target.value)} /></FieldRow>
          </div>
          <FieldRow label="Super Fund Name"><Input value={bd.super_fund || ''} onChange={e => updateField('bank_details', 'super_fund', e.target.value)} /></FieldRow>
          <FieldRow label="Super Member Number"><Input value={bd.super_member_no || ''} onChange={e => updateField('bank_details', 'super_member_no', e.target.value)} /></FieldRow>
          <FieldRow label="Tax File Number"><Input value={bd.tfn || ''} onChange={e => updateField('bank_details', 'tfn', e.target.value)} /></FieldRow>
        </div>
      );
      case 6: return (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Please read and accept the following declarations:</p>
          {[['code_of_conduct','I have read and agree to the Code of Conduct'],['ndis_compliance','I understand my obligations under NDIS Worker Compliance'],['privacy_policy','I have read and agree to the Privacy Policy'],['employment_terms','I accept the Employment Terms and Conditions']].map(([key, label]) => (
            <label key={key} className="flex items-start gap-3 cursor-pointer p-3 border rounded-lg hover:bg-muted/30">
              <Checkbox checked={dc[key] || false} onCheckedChange={v => updateField('declarations', key, v)} className="mt-0.5" />
              <span className="text-sm">{label}</span>
            </label>
          ))}
          {/* Digital Signature */}
          <div className="pt-4 border-t">
            <p className="text-sm font-medium mb-2">Digital Signature *</p>
            <p className="text-xs text-muted-foreground mb-3">Please sign below using your mouse or finger to confirm your identity and acceptance.</p>
            <SignatureCanvas
              value={dc.signature_data || ''}
              onChange={v => updateField('declarations', 'signature_data', v)}
            />
          </div>
        </div>
      );
      case 7: return (
        <div className="space-y-4">
          <p className="text-sm font-medium">Review your information before submitting:</p>
          <Card><CardContent className="py-3"><p className="text-xs font-medium text-muted-foreground">Personal</p><p className="text-sm">{pd.first_name} {pd.last_name} · {pd.email} · {pd.phone}</p></CardContent></Card>
          <Card><CardContent className="py-3"><p className="text-xs font-medium text-muted-foreground">Documents</p><p className="text-sm">{docs.length} uploaded</p></CardContent></Card>
          <Card><CardContent className="py-3"><p className="text-xs font-medium text-muted-foreground">Declarations</p><p className="text-sm">{Object.values(dc).filter(Boolean).length}/4 accepted</p></CardContent></Card>
          <Button className="w-full" size="lg" onClick={handleSubmit} disabled={saving}>{saving ? 'Submitting...' : 'Submit Onboarding'}</Button>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold" data-testid="onboarding-org-name">{organisationName} Onboarding</h1>
          <p className="text-muted-foreground">Welcome, {data.candidate?.first_name}! Complete your onboarding below.</p>
        </div>

        <StepNav current={step} steps={STEPS} progress={progress} />

        <Card>
          <CardHeader>
            <CardTitle>{STEPS[step]}</CardTitle>
            <CardDescription>Step {step + 1} of {STEPS.length}</CardDescription>
          </CardHeader>
          <CardContent>{renderStep()}</CardContent>
        </Card>

        <div className="flex justify-between mt-4">
          <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}>Previous</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => { if (STEP_KEYS[step] !== 'documents' && STEP_KEYS[step] !== 'review') saveStep(STEP_KEYS[step]); setStep(s => s + 1); }}>
              Save & Next
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
